import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { authService } from '../../services/authService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import { STORAGE_BASE_URL } from '../../config/api';

const CoursesList = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [courses, setCourses] = useState([]);
    const [myCourses, setMyCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all'); // 'all' or 'my-courses'
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadCourses();
        loadMyCourses();
    }, []);

    const loadCourses = async () => {
        try {
            setLoading(true);
            const response = await courseService.getAll();
            if (response.success) {
                setCourses(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching courses:', error);
            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMyCourses = async () => {
        try {
            const response = await courseService.getMyCourses();
            if (response.success) {
                setMyCourses(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching my courses:', error);
            setMyCourses([]);
        }
    };

    const handleCourseClick = (courseId) => {
        navigate(`/employee/courses/${courseId}`);
    };

    const handleStartCourse = async (courseId, e) => {
        e.stopPropagation();
        try {
            const response = await courseService.startCourse(courseId);
            if (response.success) {
                navigate(`/employee/courses/${courseId}`);
            }
        } catch (error) {
            console.error('Error starting course:', error);
        }
    };

    // Filter courses based on search query
    const filteredCourses = (activeTab === 'all' ? courses : myCourses).filter(course => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return course.name?.toLowerCase().includes(query) ||
               course.description?.toLowerCase().includes(query);
    });

    // Get course status for display
    const getCourseStatus = (courseId) => {
        const myCourse = myCourses.find(c => c.course?.id === courseId);
        if (!myCourse) return null;
        return {
            status: myCourse.status,
            progress: myCourse.progress,
            isStarted: myCourse.status !== 'not_started'
        };
    };

    return (
        <div className="courses-list-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <EmployeeNav user={user} />

            {/* Header */}
            <div className="px-3 mb-3 mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="mb-0">{t('employee.coursesList.title') || 'Courses'}</h4>
                    <button
                        className="btn-back"
                        onClick={() => navigate('/employee/home')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                </div>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                    {t('employee.coursesList.subtitle') || 'Browse and start learning from our courses'}
                </p>
            </div>

            {/* Search Bar */}
            <div className="search-section px-3 mb-3">
                <div className="search-bar-container position-relative">
                    <button
                        type="button"
                        className="search-btn position-absolute"
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            left: locale === 'ar' ? 'auto' : '0',
                            right: locale === 'ar' ? '0' : 'auto'
                        }}
                    >
                        <span>🔍</span>
                    </button>
                    <input
                        type="text"
                        className="search-input form-control"
                        placeholder={t('employee.coursesList.searchPlaceholder') || 'Search courses...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            paddingLeft: locale === 'ar' ? '12px' : '40px',
                            paddingRight: locale === 'ar' ? '40px' : '12px'
                        }}
                    />
                </div>
            </div>

            {/* Tabs */}
            <div className="courses-tabs px-3 mb-3">
                <div className="d-flex gap-2">
                    <button
                        className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: '8px',
                            backgroundColor: activeTab === 'all' ? 'rgb(91 137 203)' : '#f8f9fa',
                            color: activeTab === 'all' ? 'white' : '#6c757d',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {t('employee.coursesList.allCourses') || 'All Courses'}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'my-courses' ? 'active' : ''}`}
                        onClick={() => setActiveTab('my-courses')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            borderRadius: '8px',
                            backgroundColor: activeTab === 'my-courses' ? 'rgb(91 137 203)' : '#f8f9fa',
                            color: activeTab === 'my-courses' ? 'white' : '#6c757d',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {t('employee.coursesList.myCourses') || 'My Courses'}
                    </button>
                </div>
            </div>

            {/* Courses List */}
            <div className="courses-list-container px-3 mb-5">
                {loading ? (
                    <Loader size="medium" />
                ) : filteredCourses.length > 0 ? (
                    <div className="courses-grid">
                        {filteredCourses.map((courseData) => {
                            const course = courseData.course || courseData;
                            const courseStatus = getCourseStatus(course.id);
                            const isStarted = courseStatus?.isStarted || false;
                            const progress = courseStatus?.progress || 0;
                            const status = courseStatus?.status;

                            return (
                                <div
                                    key={course.id}
                                    className="course-card-list mb-3"
                                    onClick={() => handleCourseClick(course.id)}
                                    style={{
                                        border: '1px solid #e9ecef',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: 'white'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div className="d-flex">
                                        {/* Course Image */}
                                        <div className="course-image-wrapper" style={{
                                            width: '120px',
                                            height: '120px',
                                            flexShrink: 0
                                        }}>
                                            {course.image ? (
                                                <img
                                                    src={course.image.startsWith('http') ? course.image : `${STORAGE_BASE_URL}/${course.image}`}
                                                    alt={course.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundColor: 'rgb(91 137 203)',
                                                    display: course.image ? 'none' : 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: '32px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {course.name?.charAt(0).toUpperCase() || 'C'}
                                            </div>
                                        </div>

                                        {/* Course Info */}
                                        <div className="course-info-wrapper flex-grow-1 p-3" style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}>
                                            <div>
                                                <h5 className="course-name mb-1" style={{
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    marginBottom: '4px',
                                                    color: '#212529'
                                                }}>
                                                    {course.name}
                                                </h5>
                                                {course.description && (
                                                    <p className="course-description mb-2" style={{
                                                        fontSize: '13px',
                                                        color: '#6c757d',
                                                        marginBottom: '8px',
                                                        lineHeight: '1.4',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {course.description.replace(/<[^>]*>/g, '').substring(0, 100)}...
                                                    </p>
                                                )}
                                                <div className="course-meta d-flex align-items-center gap-3" style={{
                                                    fontSize: '12px',
                                                    color: '#6c757d'
                                                }}>
                                                    <span>
                                                        {course.videos_count || course.videos?.length || 0} {t('employee.home.videos') || 'videos'}
                                                    </span>
                                                    {isStarted && (
                                                        <span style={{ color: 'rgb(91 137 203)', fontWeight: '500' }}>
                                                            {progress}% {t('employee.coursesList.complete') || 'complete'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Progress Bar for Started Courses */}
                                            {isStarted && status !== 'completed' && (
                                                <div className="course-progress-mini mt-2" style={{
                                                    width: '100%',
                                                    height: '4px',
                                                    backgroundColor: '#e9ecef',
                                                    borderRadius: '2px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div
                                                        style={{
                                                            width: `${progress}%`,
                                                            height: '100%',
                                                            backgroundColor: 'rgb(91 137 203)',
                                                            transition: 'width 0.3s ease'
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Action Button */}
                                            <div className="course-action mt-2">
                                                {status === 'completed' ? (
                                                    <button
                                                        className="btn-course-status"
                                                        disabled
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            backgroundColor: '#28a745',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            cursor: 'not-allowed',
                                                            opacity: 0.8
                                                        }}
                                                    >
                                                        {t('employee.courseDetail.completed') || 'Completed'}
                                                    </button>
                                                ) : isStarted ? (
                                                    <button
                                                        className="btn-continue-course"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCourseClick(course.id);
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            backgroundColor: 'rgb(91 137 203)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('employee.courseDetail.continueCourse') || 'Continue'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn-start-course"
                                                        onClick={(e) => handleStartCourse(course.id, e)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px',
                                                            backgroundColor: 'rgb(91 137 203)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('employee.home.startCourse') || 'Start Course'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-5" style={{ color: '#6c757d' }}>
                        <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                            {searchQuery
                                ? t('employee.coursesList.noResults') || 'No courses found matching your search'
                                : activeTab === 'all'
                                ? t('employee.coursesList.noCourses') || 'No courses available'
                                : t('employee.coursesList.noMyCourses') || 'You haven\'t started any courses yet'}
                        </p>
                        {activeTab === 'my-courses' && !searchQuery && (
                            <button
                                onClick={() => setActiveTab('all')}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'rgb(91 137 203)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    marginTop: '12px'
                                }}
                            >
                                {t('employee.coursesList.browseCourses') || 'Browse Courses'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default CoursesList;

